import { zodResolver } from "@hookform/resolvers/zod"
import { auth, auth_messages } from "@utils/firebase"
import type React from "react"
import { useState } from "react"
import { useSignInWithApple, useSignInWithEmailAndPassword, useSignInWithGoogle } from "react-firebase-hooks/auth"
import { type SubmitHandler, useForm } from "react-hook-form"
import { Link } from "react-router-dom"
import { twMerge } from "tailwind-merge"
import { z } from "zod"
import { ReactComponent as GoogleIcon } from "../../assets/icons/google.svg"
import { ReactComponent as PennyLogo } from "../../assets/icons/penny_logo.svg"
import { ReactComponent as AppleIcon } from "../../assets/icons/apple.svg"
import PennyInput from "../../components/Inputs/PennyInput"

type LoginProps = {}

const loginSchema = z.object({
  email: z.string({ required_error: "Required" }).email({ message: "Must be a valid email" }),
  password: z.string({ required_error: "Required" }).min(1, { message: "Required" }),
})
type LoginSchema = z.infer<typeof loginSchema>

interface FormValues {
  email: string
  password: string
}

const Login: React.FC<LoginProps> = () => {
  const [signInWithEmailAndPassword, userSignEmailPassword, loadingSignEmailPassword, errorSignEmailPassword] =
    useSignInWithEmailAndPassword(auth)
  const [signInWithGoogle, userGoogle, loadingGoogle, errorGoogle] = useSignInWithGoogle(auth)
  const [signInWithApple, userApple, loadingApple, errorApple] = useSignInWithApple(auth)
  const [generalError, setGeneralError] = useState<string | null>(null)

  const {
    formState: { errors },
    register,
    handleSubmit,
  } = useForm<FormValues>({
    resolver: zodResolver(loginSchema),
  })

  const onSubmit: SubmitHandler<LoginSchema> = (data) => {
    setGeneralError(null)
    if (!userSignEmailPassword) {
      const { email, password } = data

      signInWithEmailAndPassword(email, password).catch((error) => {
        console.error("Login error:", error)
        setGeneralError("Failed to sign in. Please try again.")
      })
    }
  }

  const onSignInGoogle = () => {
    setGeneralError(null)
    if (!userGoogle) {
      signInWithGoogle().catch((error) => {
        console.error("Google sign-in error:", error)
        setGeneralError("Failed to sign in with Google. Please try again.")
      })
    }
  }

  const onSignInApple = () => {
    setGeneralError(null)
    if (!userApple) {
      signInWithApple().catch((error) => {
        console.error("Apple sign-in error:", error)
        setGeneralError("Failed to sign in with Apple. Please try again.")
      })
    }
  }

  return (
    <section className="w-full grow flex items-center justify-center">
      <div className="max-w-full md:max-w-[400px] w-full py-10 mx-auto md:border md:border-pn_gray-200 bg-pn_paper-1 rounded-3xl">
        <div className="w-full py-4 px-6 flex items-center justify-center">
          <PennyLogo width={81} height={32} />
        </div>

        <div className="flex flex-col gap-y-4 py-4 px-6">
          <h2 className="text-center text-pn-large-title text-pn_gray-950">Sign in</h2>
          <span className="text-center text-pn-body-regular !font-normal text-pn_gray-600">
            Continue to your Penny account
          </span>
        </div>

        {generalError && (
          <div className="px-6 mb-4">
            <div className="bg-pn_red-50 border border-pn_red-500 text-pn_red-500 p-3 rounded-lg text-pn-callout-emphasis text-center">
              {generalError}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-y-4 py-4 px-6 ">
          <div className={twMerge(errors.email?.message?.length ? "flex flex-col gap-y-2" : "block")}>
            <PennyInput
              disabled={loadingSignEmailPassword || loadingGoogle || loadingApple}
              {...register("email")}
              type="email"
              label="Email Address"
              id="email"
              name="email"
              placeholder="E-mail"
              error={errors.email?.message?.length}
            />
            {!!errors.email?.message && (
              <span className="text-pn-callout-emphasis text-pn_red-500">{errors.email.message}</span>
            )}
          </div>

          <div className={twMerge(errors.password?.message?.length ? "flex flex-col gap-y-2" : "block")}>
            <PennyInput
              disabled={loadingSignEmailPassword || loadingGoogle || loadingApple}
              {...register("password")}
              name="password"
              type="password"
              id="password"
              label="Password"
              placeholder="Password"
              error={errors.password?.message?.length}
              withToggleShowPassword
            />
            {(!!errors.password?.message || (errorSignEmailPassword?.code && !['auth/cancelled-popup-request', 'auth/popup-closed-by-user'].includes(errorSignEmailPassword.code)) || (errorGoogle?.code && !['auth/cancelled-popup-request', 'auth/popup-closed-by-user'].includes(errorGoogle.code)) || (errorApple?.code && !['auth/cancelled-popup-request', 'auth/popup-closed-by-user'].includes(errorApple.code))) && (
              <span className="text-pn-callout-emphasis text-pn_red-500">
                {errors?.password?.message ||
                  auth_messages[errorSignEmailPassword?.code as keyof typeof auth_messages] ||
                  auth_messages[errorGoogle?.code as keyof typeof auth_messages] ||
                  auth_messages[errorApple?.code as keyof typeof auth_messages] ||
                  "An error occurred. Please try again."}
              </span>
            )}
          </div>
          <Link to="/forgot-password" className="text-pn-callout-link self-end">
            Forgot Password
          </Link>
          <button
            disabled={loadingSignEmailPassword || loadingGoogle || loadingApple}
            type="submit"
            className="btn-primary-lg"
          >
            Continue
          </button>
        </form>

        <div className="pt-4 pb-6 px-6 flex items-center justify-center">
          <span className="text-pn-callout-regular text-center">
            New to Penny?{" "}
            <Link className="text-pn-callout-link" to="/signup">
              Get Started
            </Link>
          </span>
        </div>

        <div className="py-2 px-6 flex items-center gap-x-4">
          <div className="divider-dotted-gray w-full"></div>
          <span className="text-pn-caption-regular text-pn_gray-600">or</span>
          <div className="divider-dotted-gray w-full"></div>
        </div>

        <div className="flex flex-col py-4 px-6 gap-y-4">
          <button
            disabled={loadingGoogle || loadingApple || loadingSignEmailPassword}
            onClick={onSignInGoogle}
            className="btn-secondary-lg !bg-white flex items-center gap-x-4 justify-center"
          >
            <GoogleIcon />
            Continue with Google
          </button>
          <button
            disabled={loadingGoogle || loadingApple || loadingSignEmailPassword}
            onClick={onSignInApple}
            className="btn-secondary-lg !bg-pn_gray-950 !text-pn_gray-200 disabled:opacity-50 flex items-center gap-x-4 justify-center"
          >
            <AppleIcon />
            Continue with Apple
          </button>
        </div>

        <div className="py-4 px-6 flex items-center justify-center">
          <span className="text-pn-callout-regular text-center">
            By proceeding, you agree to the{" "}
            <Link to="https://www.thisispenny.com/terms-of-business" className="text-pn-callout-link">
              Terms and Conditions
            </Link>{" "}
            and{" "}
            <Link to="https://www.thisispenny.com/privacy-policy" className="text-pn-callout-link">
              Privacy Policy
            </Link>
          </span>
        </div>
      </div>
    </section>
  )
}
export default Login